import {
    createContext,
    FC,
    PropsWithChildren,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { FeedsClient } from '@stream-io/feeds-client'

import { isFetchResultSuccessful } from '../../entities/FetchResult'
import { StreamTokenClaims } from '../../entities/StreamToken/StreamToken'
import { getStreamTokenClaims, isStreamTokenExpired } from '../../entities/StreamToken/StreamTokenHelpers'
import { getStreamTokenApiCall } from '../../entities/StreamToken/StreamTokenService'
import trans from '../../helpers/trans'

const apiKey = process.env.REACT_APP_STREAM_API_KEY

interface StreamCredentials {
    token: string
    claims: StreamTokenClaims
}

interface ValkTalkFeedsClientContextValue {
    client?: FeedsClient
    userId?: string
    error?: string
}

const ValkTalkFeedsClientContext = createContext<ValkTalkFeedsClientContextValue>({})

// Resolves to `null` when the current user is not meant to be connected to Stream.
const fetchStreamCredentials = async (): Promise<StreamCredentials | null> => {
    const result = await getStreamTokenApiCall()

    if (! isFetchResultSuccessful(result)) {
        throw new Error(result.error)
    }

    if (! result.data) {
        return null
    }

    const claims = getStreamTokenClaims(result.data)

    if (! claims) {
        throw new Error('Stream token does not contain a user_id claim')
    }

    return { token: result.data, claims }
}

export const ValkTalkFeedsClientProvider: FC<PropsWithChildren> = ({ children }): ReactElement => {
    const credentialsRef = useRef<StreamCredentials | null>(null)
    const [streamUserId, setStreamUserId] = useState<string>()
    const [client, setClient] = useState<FeedsClient>()
    const [connectionError, setConnectionError] = useState<string>()

    useEffect((): (() => void) | undefined => {
        if (! apiKey) {
            return undefined
        }

        let isMounted = true

        fetchStreamCredentials()
            .then(credentials => {
                if (! isMounted) {
                    return
                }

                credentialsRef.current = credentials

                if (credentials) {
                    setStreamUserId(credentials.claims.userId)
                } else {
                    setConnectionError(trans('entities.valkTalkPost.errors.noStreamAccess'))
                }
            })
            .catch((error: unknown) => {
                console.error(error)

                if (isMounted) {
                    setConnectionError(trans('entities.valkTalkPost.errors.streamConnectionFailed'))
                }
            })

        return () => {
            isMounted = false
        }
    }, [])

    // Stream calls this on connect and whenever the token expires, so the cached token is reused until then.
    const tokenProvider = useCallback(async (): Promise<string> => {
        const cached = credentialsRef.current

        if (cached && ! isStreamTokenExpired(cached.claims)) {
            return cached.token
        }

        const credentials = await fetchStreamCredentials()

        if (! credentials) {
            throw new Error('Could not refresh Stream token')
        }

        credentialsRef.current = credentials

        return credentials.token
    }, [])

    useEffect((): (() => void) | undefined => {
        if (! apiKey || ! streamUserId) {
            return undefined
        }

        const feedsClient = new FeedsClient(apiKey)
        // Only the id: the back-end sets the user's name in Stream, and users aren't allowed to update themselves
        const connection = feedsClient.connectUser({ id: streamUserId }, tokenProvider)
        let isMounted = true

        connection
            .then(() => {
                if (isMounted) {
                    setConnectionError(undefined)
                    setClient(feedsClient)
                }
            })
            .catch((error: unknown) => {
                console.error(error)

                if (isMounted) {
                    setConnectionError(trans('entities.valkTalkPost.errors.streamConnectionFailed'))
                }
            })

        return () => {
            isMounted = false
            setClient(undefined)
            connection
                .then(() => feedsClient.disconnectUser())
                .catch(() => undefined)
        }
    }, [streamUserId, tokenProvider])

    const error = apiKey ? connectionError : trans('entities.valkTalkPost.errors.missingStreamConfiguration')
    const value = useMemo(() => ({ client, userId: streamUserId, error }), [client, streamUserId, error])

    return (
        <ValkTalkFeedsClientContext.Provider value={value}>
            {children}
        </ValkTalkFeedsClientContext.Provider>
    )
}

export const useValkTalkFeedsClient = (): ValkTalkFeedsClientContextValue => useContext(ValkTalkFeedsClientContext)
